/**
 * Activity Voucher — premium operational layout (Phase 8.41 refactor).
 *
 * PDF rendering rule: html2canvas / Playwright print do NOT support flexbox
 * reliably (icons drift relative to baseline, gaps render unpredictably).
 * This file uses ONLY block / inline-block / table / table-cell / grid
 * layouts. Do NOT introduce display:flex or any className containing the
 * word "flex" anywhere in this file — every icon-text alignment goes
 * through the IconTextRow CSS-table helper from VoucherPrimitives.
 *
 * Header & branding stay untouched (handled by BrandedVoucherLayout). This
 * component only redesigns the BODY rendered between the header and footer.
 *
 * QR / barcode policy: original codes are passed VERBATIM to VoucherBarcodeStrip
 * which uses qrcode.react / react-barcode. The decoded payload from the source
 * supplier voucher is NEVER regenerated, redrawn, AI-recreated, stylized,
 * compressed or resized disproportionately.
 *
 * Refactor history: shared primitives (IconTextRow, SectionCard, InfoBadge,
 * BulletList, InlineHtmlOrText, helpers) were moved to VoucherPrimitives.js
 * and inline-edit components (InlineEditableField, EditableArraySectionCard,
 * RefRow) were moved to VoucherEditComponents.js so they can be reused by
 * upcoming Hotel/Flight voucher templates.
 */
import React from 'react';
import {
  CheckCircle2, XCircle, MapPin, Clock, Users, Calendar, Phone,
  Info, AlertTriangle, FileText, Hash, Sparkles,
} from 'lucide-react';
import VoucherBarcodeStrip from './VoucherBarcodeStrip';
import PerGuestCodesGrid from './PerGuestCodesGrid';
import { formatVoucherDate } from '../../lib/voucherDate';
import {
  IconTextRow,
  SectionCard,
  InfoBadge,
  InlineHtmlOrText,
  toArray,
  has,
} from './VoucherPrimitives';
import {
  InlineEditableField,
  EditableArraySectionCard,
  RefRow,
} from './VoucherEditComponents';

// ---------------------------------------------------------------------------
// PaxSummary — Total Pax row uses IconTextRow. Lead-guest name supports
// inline editing when `editable` is true.
// ---------------------------------------------------------------------------
const PaxSummary = ({ a, accent, editable = false, locked = false, onFieldSave }) => {
  const total = a.totalPax || a.participants || ((a.adults || 0) + (a.children || 0) + (a.infants || 0));
  const parts = [];
  if (a.adults) parts.push(`${a.adults} Adult${a.adults > 1 ? 's' : ''}`);
  if (a.children) parts.push(`${a.children} Child${a.children > 1 ? 'ren' : ''}`);
  if (a.infants) parts.push(`${a.infants} Infant${a.infants > 1 ? 's' : ''}`);
  const split = parts.length ? parts.join(' + ') : null;
  return (
    <div className="space-y-2">
      {(has(a.leadGuestName) || editable || locked) && (
        <div>
          <span style={{ display: 'block', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.18em', fontWeight: 600, color: accent }}>Lead Guest</span>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
            <InlineEditableField
              value={a.leadGuestName || ''}
              variant="text"
              editable={editable}
              locked={locked}
              onSave={(v) => onFieldSave?.('leadGuestName', v)}
              textareaRows={1}
              ariaLabel="Edit lead guest name"
              testIdPrefix="activity-lead-guest-editable"
              displayStyle={{ display: 'inline' }}
            />
          </div>
        </div>
      )}
      {Array.isArray(a.guestNames) && a.guestNames.filter(Boolean).length > 1 && (
        <div>
          <span style={{ display: 'block', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.18em', fontWeight: 600, color: accent }}>Guests</span>
          <ul style={{ marginTop: '4px', fontSize: '14px', color: '#334155', lineHeight: 1.4, listStyle: 'none', padding: 0 }}>
            {a.guestNames.filter(Boolean).map((g, i) => <li key={i}>· {g}</li>)}
          </ul>
        </div>
      )}
      {(has(total) || split) && (
        <div style={{ paddingTop: '4px' }}>
          <IconTextRow icon={Users} iconStyle={{ color: '#94a3b8' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
              {has(total) ? `Total Pax: ${total}` : ''}{has(total) && split ? '  ·  ' : ''}{split || ''}
            </span>
          </IconTextRow>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ActivityVoucherBody({ voucher, company, editable = false, locked = false, onFieldSave }) {
  const a = voucher?.activity || {};
  const accent = company?.accentColor || '#595A5B';
  const orig = voucher?.originalCode;
  const allCodes = Array.isArray(voucher?.allCodes) && voucher.allCodes.length > 0
    ? voucher.allCodes
    : (orig?.value ? [{ kind: orig.kind, value: orig.value, format: orig?.format }] : []);

  const inclusions = toArray(a.inclusions);
  const exclusions = toArray(a.exclusions);
  const redemptionPoints = Array.isArray(a.redemptionPoints) ? a.redemptionPoints.filter((p) => p && (p.name || p.address)) : [];

  const operatorName = voucher?.operatorOverride || a.operator || '';
  const showOperator = voucher?.showOperator !== false && has(operatorName);
  const activityName = a.activityName || a.name || a.title || a.tourName || '(Activity name pending)';
  const city = a.city || a.destination || voucher?.destination || '';

  const guestNames = Array.isArray(a.guestNames) ? a.guestNames.filter(Boolean) : [];
  const leadName = a.leadGuestName || guestNames[0] || voucher?.guestName || '';
  let perGuestPairs = null;
  if (allCodes.length > 1) {
    if (guestNames.length === allCodes.length) {
      perGuestPairs = guestNames.map((name, i) => ({ name, code: allCodes[i] }));
    } else if (has(leadName)) {
      perGuestPairs = allCodes.map((code) => ({ name: leadName, code }));
    }
  }

  return (
    <div
      data-testid="activity-voucher-body"
      className="text-slate-800"
      style={{ paddingTop: '4mm', paddingBottom: '4mm' }}
    >
      {/* SECTION 1 — HERO HEADER
          Note: the big activity-name title and the small "Activity Voucher"
          label both live in the OUTER hero (BrandedVoucherLayout) to avoid
          rendering the name twice. This inner hero starts directly with the
          editable date + location + guest + QR block. */}
      <div
        className="break-inside-avoid pdf-no-break"
        style={{
          display: 'block',
          marginBottom: '20px',
          maxWidth: '100%',
          pageBreakInside: 'avoid',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        <div style={{ display: 'block', marginTop: '0px', fontSize: '14px', color: '#475569' }}>
          {(has(a.date) || editable || locked) && (
            <div style={{ marginBottom: '6px', lineHeight: '22px' }}>
              <IconTextRow icon={Calendar} iconStyle={{ color: '#94a3b8' }}>
                {has(a.time) && !editable && !locked ? (
                  <span style={{ fontWeight: 500, color: '#0f172a', lineHeight: '22px' }}>
                    {[formatVoucherDate(a.date), a.time].filter(Boolean).join(' · ')}
                  </span>
                ) : (
                  <InlineEditableField
                    value={a.date || ''}
                    variant="date"
                    editable={editable}
                    locked={locked}
                    onSave={(v) => onFieldSave?.('date', v)}
                    formatValue={formatVoucherDate}
                    ariaLabel="Edit activity date"
                    testIdPrefix="activity-date-editable"
                    displayClassName="font-medium text-slate-900"
                    displayStyle={{ lineHeight: '22px' }}
                  />
                )}
              </IconTextRow>
            </div>
          )}
          {has(city) && (
            <div style={{ marginBottom: '6px', lineHeight: '22px' }}>
              <IconTextRow icon={MapPin} iconStyle={{ color: '#94a3b8' }}>
                <span style={{ lineHeight: '22px' }}>{city}</span>
              </IconTextRow>
            </div>
          )}
        </div>

        {/* Guest information block */}
        <div className="mt-4 p-4 rounded-xl bg-white border border-slate-200">
          <PaxSummary a={a} accent={accent} editable={editable} locked={locked} onFieldSave={onFieldSave} />
        </div>

        {/* QR / BARCODE BLOCK */}
        {allCodes.length > 0 && (
          <div
            className="mt-5 p-5 rounded-xl bg-white border border-slate-200 break-inside-avoid pdf-no-break"
            data-testid="activity-codes-block"
            style={{ pageBreakInside: 'avoid' }}
          >
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-3 text-center" style={{ color: accent }}>
              Scan to validate
            </div>
            {perGuestPairs ? (
              <PerGuestCodesGrid pairs={perGuestPairs} accent={accent} labelPrefix="Guest" />
            ) : (
              <div style={{ display: 'block', textAlign: 'center' }}>
                {allCodes.map((c, i) => (
                  <div key={i} style={{ display: 'inline-block', verticalAlign: 'top', margin: '12px' }}>
                    {has(leadName) && (
                      <div
                        className="font-semibold"
                        style={{
                          fontSize: '12px',
                          letterSpacing: '0.12em',
                          color: '#0f172a',
                          marginBottom: '8px',
                          textAlign: 'center',
                        }}
                        data-testid={`activity-code-guest-name-${i}`}
                      >
                        {leadName}
                      </div>
                    )}
                    <VoucherBarcodeStrip
                      value={c.value}
                      imageBase64={c.imageBase64}
                      kind={c.kind || 'barcode'}
                      accent={accent}
                      label={(c.kind || 'code').toString().toUpperCase()}
                      size="prominent"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Voucher references */}
        <div className="mt-4 space-y-1.5" data-testid="activity-refs">
          <RefRow label="TRV Reference No" value={voucher?.trvRef || a.voucherRef || voucher?.voucherId} accent={accent} />
          <RefRow
            label="Partner Reference ID"
            value={a.bookingReferenceId || a.partnerRef || a.bookingRef}
            accent={accent}
            editable={editable}
            locked={locked}
            fieldKey="bookingReferenceId"
            onFieldSave={onFieldSave}
          />
          <RefRow
            label="Operator Ref"
            value={a.operatorRef || a.confirmationNumber}
            accent={accent}
            editable={editable}
            locked={locked}
            fieldKey="operatorRef"
            onFieldSave={onFieldSave}
          />
          {showOperator && <RefRow label="Operator" value={operatorName} accent={accent} />}
        </div>
      </div>

      {/* SECTION 3 — QUICK INFO STRIP — inline-block wrapping (no flex). */}
      {(() => {
        // For multi-point fields that are now arrays (validity, reportingTime,
        // openingHours) compact down to the first item for the badge strip.
        // The full bulleted list still renders in the HowToUseSection card below.
        const first = (v) => Array.isArray(v) ? (v.filter(Boolean)[0] || '') : v;
        return (
          <div
            className="mb-5 break-inside-avoid pdf-no-break"
            data-testid="activity-info-strip"
            style={{
              display: 'block',
              breakBefore: 'avoid',
              pageBreakBefore: 'avoid',
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            }}
          >
            <InfoBadge label="Voucher Type" value={a.voucherType} accent={accent} />
            <InfoBadge label="Validity" value={first(a.validity)} accent={accent} />
            <InfoBadge label="Reporting Time" value={first(a.reportingTime)} accent={accent} />
            <InfoBadge label="Duration" value={a.duration} accent={accent} />
            <InfoBadge label="Confirmation" value={a.instantConfirmation} accent={accent} />
            <InfoBadge label="Reservation" value={a.reservationRequired} accent={accent} />
          </div>
        );
      })()}

      {/* SECTION 4 — ABOUT THIS ACTIVITY */}
      {(has(a.overview) || has(a.description)) && (
        <SectionCard title="About this activity" icon={Info} accent={accent} testId="activity-about">
          <InlineHtmlOrText value={a.overview || a.description} />
        </SectionCard>
      )}

      {/* SECTION 4B — HIGHLIGHTS */}
      <EditableArraySectionCard
        title="Highlights"
        icon={Sparkles}
        accent={accent}
        testId="activity-highlights"
        value={a.highlights}
        fieldKey="highlights"
        editable={editable}
        locked={locked}
        onFieldSave={onFieldSave}
        kind="plain"
      />

      {/* SECTION 5 — INCLUDED / NOT INCLUDED (CSS Grid is allowed; only flex banned) */}
      {(inclusions.length > 0 || exclusions.length > 0 || editable) && (
        <div
          className="break-inside-avoid pdf-no-break"
          data-testid="activity-included-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            columnGap: '16px',
            rowGap: '16px',
            marginBottom: '16px',
            pageBreakInside: 'avoid',
            breakInside: 'avoid',
            maxWidth: '100%',
          }}
        >
          <EditableArraySectionCard
            title="What's Included"
            icon={CheckCircle2}
            accent={accent}
            testId="activity-included"
            className="mb-0 bg-emerald-50/60 border-emerald-100"
            value={a.inclusions}
            fieldKey="inclusions"
            editable={editable}
            locked={locked}
            onFieldSave={onFieldSave}
            kind="include"
          />
          <EditableArraySectionCard
            title="What's Not Included"
            icon={XCircle}
            accent={accent}
            testId="activity-excluded"
            className="mb-0 bg-rose-50/60 border-rose-100"
            value={a.exclusions}
            fieldKey="exclusions"
            editable={editable}
            locked={locked}
            onFieldSave={onFieldSave}
            kind="exclude"
          />
        </div>
      )}

      {/* SECTION 6 — IMPORTANT INFORMATION (each block + hotline is its own SectionCard) */}
      <ImportantInformation a={a} accent={accent} showOperator={showOperator} operatorName={operatorName} editable={editable} locked={locked} onFieldSave={onFieldSave} />

      {/* SECTION 7 — ADDRESS & LOCATION */}
      {(has(a.venueName) || has(a.address) || has(a.meetingPoint) || has(a.pickupPoint) || redemptionPoints.length > 0 || has(a.howToGetThere) || editable || locked) && (
        <SectionCard title="Activity Location" icon={MapPin} accent={accent} testId="activity-location">
          <div className="card mb-3 p-3 rounded-lg bg-white border border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1" style={{ color: accent }}>
              {has(a.venueName) ? 'Venue' : 'Activity'}
            </div>
            <div className="text-sm font-semibold text-slate-900 break-words">
              <InlineEditableField
                value={a.venueName || activityName}
                variant="text"
                editable={editable}
                locked={locked}
                onSave={(v) => onFieldSave?.('venueName', v)}
                textareaRows={1}
                ariaLabel="Edit venue name"
                testIdPrefix="activity-venue-name-editable"
                displayStyle={{ display: 'inline' }}
              />
            </div>
            {(has(a.address) || editable || locked) && (
              <div className="text-sm text-slate-600 mt-1 whitespace-pre-line break-words" data-testid="activity-location-address">
                <InlineEditableField
                  value={a.address || ''}
                  variant="text"
                  editable={editable}
                  locked={locked}
                  onSave={(v) => onFieldSave?.('address', v)}
                  textareaRows={2}
                  ariaLabel="Edit address"
                  testIdPrefix="activity-address-editable"
                  displayStyle={{ display: 'inline' }}
                />
              </div>
            )}
            {(has(a.city) || has(a.country)) && (
              <div className="text-sm text-slate-600 mt-0.5 break-words" data-testid="activity-location-city-country">
                {[a.city, a.country].filter(has).join(', ')}
              </div>
            )}
            {has(a.postalCode) && (
              <div className="text-xs text-slate-500 mt-0.5 break-words" data-testid="activity-location-postal-code">
                {a.postalCode}
              </div>
            )}
            {has(a.venueName) && !has(a.address) && !has(a.city) && !has(a.postalCode) && !editable && (
              <div className="text-xs italic mt-1" style={{ color: '#9ca3af' }} data-testid="activity-location-no-address-note">
                Address not available in supplier document
              </div>
            )}
          </div>

          {(has(a.meetingPoint) || has(a.pickupPoint) || redemptionPoints.length > 0) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                columnGap: '12px',
                rowGap: '12px',
                marginBottom: '12px',
                maxWidth: '100%',
              }}
            >
              {(has(a.meetingPoint) || has(a.pickupPoint)) && (
                <div className="p-3 rounded-lg bg-white border border-slate-200" style={{ minWidth: 0 }}>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1" style={{ color: accent }}>Meeting Point</div>
                  <div className="text-sm text-slate-700 whitespace-pre-line break-words">{a.meetingPoint || a.pickupPoint}</div>
                </div>
              )}
              {redemptionPoints.map((p, i) => (
                <div key={i} className="p-3 rounded-lg bg-white border border-slate-200" style={{ minWidth: 0 }}>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1" style={{ color: accent }}>Redemption Point {i + 1}</div>
                  {has(p.name) && <div className="text-sm font-semibold text-slate-900 break-words">{p.name}</div>}
                  {has(p.address) && <div className="text-sm text-slate-600 mt-0.5 whitespace-pre-line break-words">{p.address}</div>}
                </div>
              ))}
            </div>
          )}

          {has(a.howToGetThere) && !Array.isArray(a.howToGetThere) && (
            <div className="card p-3 rounded-lg bg-white border border-slate-200" data-testid="activity-how-to-get-there">
              <div style={{ marginBottom: '6px' }}>
                <IconTextRow icon={MapPin} iconStyle={{ opacity: 0.7, color: accent }}>
                  <h4 className="uppercase font-semibold" style={{ fontSize: '11px', letterSpacing: '0.18em', lineHeight: 1, color: accent, margin: 0 }}>How to get there</h4>
                </IconTextRow>
              </div>
              <InlineHtmlOrText value={a.howToGetThere} />
            </div>
          )}
        </SectionCard>
      )}

      {/* SECTION 7A1 — HOW TO GET THERE (its own top-level SectionCard when
          the field is an array. Spec: appears as its own section card,
          hides cleanly when the source PDF has no directions data.) */}
      {(Array.isArray(a.howToGetThere) || editable || locked) && (
        <EditableArraySectionCard
          title="How to Get There"
          icon={MapPin}
          accent={accent}
          testId="activity-how-to-get-there-array"
          value={a.howToGetThere}
          fieldKey="howToGetThere"
          editable={editable}
          locked={locked}
          onFieldSave={onFieldSave}
          kind="plain"
        />
      )}

      {/* SECTION 7B — PROHIBITIONS & LIMITATIONS (each its own SectionCard) */}
      <ProhibitionsLimitations a={a} accent={accent} editable={editable} locked={locked} onFieldSave={onFieldSave} />

      {/* SECTION 7C — HOW TO USE (each block its own SectionCard) */}
      <HowToUseSection a={a} accent={accent} editable={editable} locked={locked} onFieldSave={onFieldSave} />

      {/* SECTION 7D — CONTACT DETAILS */}
      <ContactDetailsSection a={a} accent={accent} />

      {/* SECTION 8 — TERMS & CONDITIONS */}
      {(has(a.terms) || has(a.cancellationPolicy) || has(a.cancellationTerms) || has(a.confirmationRequired) || has(a.confirmationDetails)) && (
        <StructuredTerms a={a} accent={accent} showCancellation={!voucher?.hideCancellationPolicy} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Important information — split into individual SectionCards (one per block
// + Hotline). Each is now a top-level card with the icon in its title bar.
// ---------------------------------------------------------------------------

const IMPORTANT_BLOCKS = [
  { key: 'eligibility',           label: 'Eligibility',                icon: Info },
  { key: 'additionalInfo',        label: 'Additional Information',     icon: Info },
  { key: 'operationalNotes',      label: 'Operational Notes',          icon: AlertTriangle },
  { key: 'reportingInstructions', label: 'Reporting Instructions',     icon: Clock },
];

function ImportantInformation({ a, accent, showOperator, operatorName, editable = false, locked = false, onFieldSave }) {
  const blocks = (editable || locked) ? IMPORTANT_BLOCKS : IMPORTANT_BLOCKS.filter((b) => has(a[b.key]));
  const showHotline = has(a.hotline) || has(a.emergencyContact) || has(a.operatorPhone);
  if (blocks.length === 0 && !showHotline) return null;
  return (
    <>
      {blocks.map((b) => (
        <EditableArraySectionCard
          key={b.key}
          title={b.label}
          icon={b.icon}
          accent={accent}
          testId={`activity-info-${b.key}`}
          value={a[b.key]}
          fieldKey={b.key}
          editable={editable}
          locked={locked}
          onFieldSave={onFieldSave}
          kind="plain"
        />
      ))}
      {showHotline && (
        <SectionCard
          title="Hotline / Emergency"
          icon={Phone}
          accent={accent}
          testId="activity-hotline"
          className="bg-amber-50 border-amber-200"
        >
          {has(a.hotline) && <div className="font-semibold text-slate-900 break-words" style={{ marginBottom: '2px' }}>{a.hotline}</div>}
          {has(a.emergencyContact) && a.emergencyContact !== a.hotline && (
            <div className="text-slate-700 break-words" style={{ marginBottom: '2px' }}>{a.emergencyContact}</div>
          )}
          {has(a.operatorPhone) && a.operatorPhone !== a.hotline && (
            <div className="text-slate-700 break-words">
              {showOperator && has(operatorName) ? operatorName + ' · ' : ''}{a.operatorPhone}
            </div>
          )}
        </SectionCard>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// StructuredTerms — Confirmation / Cancellation / T&C as separate SectionCards.
// ---------------------------------------------------------------------------
function StructuredTerms({ a, accent, showCancellation }) {
  const text = String(a.terms || '');
  const long = text.length > 600;
  const [open, setOpen] = React.useState(!long);
  const hasConfirmation = has(a.confirmationRequired) || has(a.confirmationDetails);
  const hasCancellation = showCancellation && (has(a.cancellationPolicy) || has(a.cancellationTerms));
  return (
    <>
      {hasConfirmation && (
        <SectionCard
          title="Confirmation"
          icon={CheckCircle2}
          accent={accent}
          testId="activity-terms-confirmation"
        >
          <InlineHtmlOrText value={a.confirmationRequired || a.confirmationDetails} />
        </SectionCard>
      )}
      {hasCancellation && (
        <SectionCard
          title="Cancellations"
          icon={AlertTriangle}
          accent={accent}
          testId="activity-terms-cancellation"
        >
          <InlineHtmlOrText value={a.cancellationTerms || a.cancellationPolicy} />
        </SectionCard>
      )}
      {has(text) && (
        <SectionCard
          title="Terms & Conditions"
          icon={Hash}
          accent={accent}
          testId="activity-terms"
        >
          <div
            className={`text-xs leading-relaxed text-slate-600 ${open ? '' : 'max-h-32 overflow-hidden relative'}`}
          >
            <InlineHtmlOrText value={text} className="text-xs" />
            {!open && (
              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
            )}
          </div>
          {long && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: accent }}
              data-testid="activity-terms-toggle"
            >
              {open ? 'Collapse' : 'View Full Terms'}
            </button>
          )}
        </SectionCard>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Prohibitions & Limitations — rendered side-by-side in a 2-column CSS grid
// per the operational-voucher spec.
// ---------------------------------------------------------------------------
function ProhibitionsLimitations({ a, accent, editable = false, locked = false, onFieldSave }) {
  if (!has(a.prohibitions) && !has(a.limitations) && !editable && !locked) return null;
  return (
    <div
      className="break-inside-avoid pdf-no-break"
      data-testid="activity-prohibitions-limitations-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        columnGap: '16px',
        rowGap: '16px',
        marginBottom: '16px',
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
        maxWidth: '100%',
      }}
    >
      <EditableArraySectionCard
        title="Prohibitions"
        icon={XCircle}
        accent={accent}
        testId="activity-prohibitions"
        className="mb-0 bg-rose-50/40 border-rose-100"
        value={a.prohibitions}
        fieldKey="prohibitions"
        editable={editable}
        locked={locked}
        onFieldSave={onFieldSave}
        kind="exclude"
      />
      <EditableArraySectionCard
        title="Limitations"
        icon={AlertTriangle}
        accent={accent}
        testId="activity-limitations"
        className="mb-0 bg-amber-50/40 border-amber-100"
        value={a.limitations}
        fieldKey="limitations"
        editable={editable}
        locked={locked}
        onFieldSave={onFieldSave}
        kind="plain"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// How to Use — split into individual SectionCards per block.
// ---------------------------------------------------------------------------
const HOW_TO_USE_BLOCKS = [
  { key: 'usageValidity',       label: 'Usage Validity',     icon: Clock,        altKey: 'validity' },
  { key: 'openingHours',        label: 'Operational Hours',  icon: Clock },
  { key: 'reportingTime',       label: 'Reporting Time',     icon: Clock },
  { key: 'voucherTypeDetails',  label: 'Voucher Type',       icon: FileText,     altKey: 'voucherType' },
  { key: 'howToRedeem',         label: 'How to Redeem',      icon: CheckCircle2, altKey: 'redemptionProcess' },
];

function HowToUseSection({ a, accent, editable = false, locked = false, onFieldSave }) {
  const blocks = HOW_TO_USE_BLOCKS.map((b) => ({
    ...b,
    value: a[b.key] || (b.altKey ? a[b.altKey] : '') || (b.key === 'howToRedeem' ? a.usageInstructions : ''),
  })).filter((b) => editable || locked || has(b.value));
  if (blocks.length === 0) return null;
  return (
    <>
      {blocks.map((b) => (
        <EditableArraySectionCard
          key={b.key}
          title={b.label}
          icon={b.icon}
          accent={accent}
          testId={`activity-how-${b.key}`}
          value={b.value}
          fieldKey={b.key}
          editable={editable}
          locked={locked}
          onFieldSave={onFieldSave}
          kind="plain"
        />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Contact Details — each row label+value via CSS-table (no flex).
// ---------------------------------------------------------------------------
function ContactDetailsSection({ a, accent }) {
  const explicit = Array.isArray(a.contactDetails) ? a.contactDetails.filter(Boolean) : [];
  const auto = [];
  if (has(a.operatorPhone)) auto.push({ label: 'Operator Phone', value: a.operatorPhone });
  if (has(a.operatorEmail)) auto.push({ label: 'Operator Email', value: a.operatorEmail });
  if (has(a.guidePhone))    auto.push({ label: 'Guide Phone',    value: a.guidePhone });
  if (has(a.guideName) && !auto.find((x) => x.label === 'Guide Phone')) auto.push({ label: 'Guide', value: a.guideName });
  const list = explicit.length > 0
    ? explicit.map((c) => (typeof c === 'string' ? { label: 'Contact', value: c } : c))
    : auto;
  if (list.length === 0) return null;
  return (
    <SectionCard title="Contact Details" icon={Phone} accent={accent} testId="activity-contact-details">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          columnGap: '8px',
          rowGap: '8px',
          maxWidth: '100%',
        }}
      >
        {list.map((c, i) => (
          <div
            key={i}
            className="p-2.5 rounded-lg bg-white border border-slate-200"
            style={{ display: 'table', width: '100%', minWidth: 0 }}
          >
            <span
              className="uppercase font-semibold"
              style={{
                display: 'table-cell',
                verticalAlign: 'middle',
                whiteSpace: 'nowrap',
                paddingRight: '8px',
                fontSize: '10px',
                letterSpacing: '0.18em',
                color: accent,
              }}
            >
              {c.label || 'Contact'}
            </span>
            <span
              style={{
                display: 'table-cell',
                verticalAlign: 'middle',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                fontSize: '14px',
                color: '#1e293b',
              }}
            >
              {c.value}
            </span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
